import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { ErrorState } from "../../components/ui/AsyncState";
import { ErrorIcon, SearchIcon } from "../../components/ui/Icons";
import { InfoBadge } from "../../components/ui/InfoBadge";

const STARTING_TITLES = [
  "Verifying...",
  "Checking for fraud...",
  "Setting things up...",
  "Loading task...",
];
const STARTING_TITLE_DELAY_MIN_MS = 1000;
const STARTING_TITLE_DELAY_MAX_MS = 6000;

export function LoadingTaskCard({
  shouldReduceMotion,
}: {
  shouldReduceMotion: boolean;
}) {
  return (
    <div className="task-reveal-message-stage">
      <div className="task-reveal-message-shell">
        <div className="task-reveal-message">
          <div className="task-reveal-message__eyebrow">Your next task</div>
          <h1 className="task-reveal-message__title title-lg">
            Finding the right match...
          </h1>
          <p className="task-reveal-message__description">
            Trying to find the best task for you from our library.
          </p>
          <div className="task-reveal-loading__status">
            <div className="task-reveal-loading__bar-track" aria-hidden="true">
              <div
                className={`task-reveal-loading__bar${
                  shouldReduceMotion ? " task-reveal-loading__bar--static" : ""
                }`}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function StartingTaskCard({
  shouldReduceMotion,
}: {
  shouldReduceMotion: boolean;
}) {
  const [titleIndex, setTitleIndex] = useState(0);

  useEffect(() => {
    if (shouldReduceMotion || titleIndex >= STARTING_TITLES.length - 1) {
      return undefined;
    }

    const delay =
      STARTING_TITLE_DELAY_MIN_MS +
      Math.random() *
        (STARTING_TITLE_DELAY_MAX_MS - STARTING_TITLE_DELAY_MIN_MS);

    const timeoutId = window.setTimeout(() => {
      setTitleIndex((current) =>
        Math.min(current + 1, STARTING_TITLES.length - 1),
      );
    }, delay);

    return () => window.clearTimeout(timeoutId);
  }, [shouldReduceMotion, titleIndex]);

  return (
    <div className="task-reveal-message-stage">
      <div className="task-reveal-message-shell">
        <div className="task-reveal-message">
          <div className="task-reveal-message__eyebrow">Starting your task</div>
          <AnimatePresence initial={false} mode="wait">
            <motion.h1
              key={STARTING_TITLES[titleIndex]}
              animate={{ opacity: 1, y: 0 }}
              className="task-reveal-message__title"
              exit={{ opacity: 0, y: -8 }}
              initial={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.24, ease: "easeOut" }}
            >
              {STARTING_TITLES[titleIndex]}
            </motion.h1>
          </AnimatePresence>
          <p className="task-reveal-message__description">
            We&apos;re getting things ready for you now.
          </p>
          {/* <div className="task-reveal-loading__status">
            <div className="task-reveal-loading__bar-track" aria-hidden="true">
              <div
                className={`task-reveal-loading__bar${
                  shouldReduceMotion ? " task-reveal-loading__bar--static" : ""
                }`}
              />
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
}

export function ErrorTaskMessage({
  error,
  onAction,
}: {
  error: unknown;
  onAction: () => void;
}) {
  return (
    <div className="task-reveal-message-stage">
      <div className="task-reveal-message-shell">
        <div className="task-reveal-message task-reveal-message--error">
          <div className="task-reveal-message__icon" aria-hidden="true">
            <ErrorIcon />
          </div>
          <div className="task-reveal-message__eyebrow">Task reveal paused</div>
          <h1 className="task-reveal-message__title">We hit a small snag.</h1>
          <p className="task-reveal-message__description">
            Refresh to try again and we&apos;ll fetch another task for you.
          </p>
          <div className="task-reveal-message__error">
            <ErrorState error={error} title="Could not load task" />
          </div>
          <button className="btn btn-accent" onClick={onAction} type="button">
            Try again
          </button>
        </div>
      </div>
    </div>
  );
}

export function EmptyTaskMessage({
  onAction,
}: {
  onAction: (reset?: boolean) => void;
}) {
  return (
    <div className="task-reveal-message-stage">
      <div className="task-reveal-message-shell">
        <div className="task-reveal-message task-reveal-message--empty">
          <div className="task-reveal-message__icon" aria-hidden="true">
            <SearchIcon />
          </div>
          <div className="task-reveal-message__eyebrow">Nothing yet</div>
          <h1 className="task-reveal-message__title">
            No tasks available right now.
          </h1>
          <p className="task-reveal-message__description">
            Check back soon for fresh opportunities tailored to you. New tasks
            are added all the time!
          </p>
          <div
            style={{
              display: "flex",
              gap: 8,
              justifyContent: "end",
              width: "100%",
            }}
          >
            <button
              className="btn btn-outline"
              onClick={() => onAction(true)}
              style={{ width: 100 }}
              type="button"
            >
              Reset
              <InfoBadge>Will show all your "Not for me" tasks again</InfoBadge>
            </button>
            <button
              className="btn btn-accent"
              onClick={() => onAction()}
              style={{ width: 100 }}
              type="button"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
